import {
  DocumentReference,
  MappedDocument,
} from "us.collection.input-output/constants";

/**
 * Normalizes keys into an array format.
 * Supports single values, arrays, and null/undefined inputs.
 *
 * @param keys - The keys to normalize (can be a single value, array, or null/undefined)
 * @returns An array containing the keys, or an empty array if keys is null/undefined
 */
export const normalizeToArray = <T>(keys: T | T[] | null | undefined): T[] => {
  if (keys == null) {
    return [];
  }

  return Array.isArray(keys) ? keys : [keys];
};
/**
 * Numeric comparator for sorting.
 * Validates and compares numbers, returning 0 for invalid/NaN values.
 */
const numericComparator = (a: number, b: number): number => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return 0;
  }
  return a - b;
};

/**
 * Comparator that handles both numeric and lexicographic sorting.
 * Attempts numeric comparison first, falls back to string comparison.
 * Pre-converts values to avoid repeated conversions during sort.
 */
const mixedComparator = (a: unknown, b: unknown): number => {
  const aNum = Number(a);
  const bNum = Number(b);

  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }

  return String(a).localeCompare(String(b));
};
/**
 * Sorts an array of mixed types using numeric comparison for numeric values
 * and lexicographic comparison for strings.
 *
 * @param keys - The array of keys to sort
 * @returns A new sorted array
 */
export const sortKeys = <T extends string | number | { toString(): string }>(
  keys: T[],
): T[] => {
  if (!Array.isArray(keys) || keys.length === 0) {
    return [...keys];
  }

  return [...keys].sort((a, b) => mixedComparator(a, b));
};

/**
 * Sorts an array of documents by their documentId in ascending numeric order.
 * Validates documentIds are finite numbers before comparison to prevent NaN issues.
 *
 * @param data - The array of documents to sort
 * @returns A new sorted array, or an empty array if data is not an array
 */
export const sortDocumentsByDocumentId = <T extends DocumentReference>(
  data: T[] | any,
): T[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return [...data].sort((a, b) => {
    const aId = Number(a?.documentId);
    const bId = Number(b?.documentId);
    return numericComparator(aId, bId);
  });
};

/**
 * Maps sorted documents with sorted keys, creating key-value pairs.
 * Note: This function assumes sortedData and sortedKeys have matching lengths.
 * If keys are exhausted, it falls back to numeric index + 1 as a stable fallback key.
 * A length mismatch triggers a console warning for debugging.
 *
 * @param sortedData - The array of sorted documents
 * @param sortedKeys - The array of sorted keys to map to documents
 * @returns An array of MappedDocument objects with documentId and corresponding key
 */
export const getMappedDocumentsIds = (
  sortedData: DocumentReference[],
  sortedKeys: React.Key[],
): MappedDocument[] => {
  if (!Array.isArray(sortedData) || !Array.isArray(sortedKeys)) {
    return [];
  }

  if (sortedData.length !== sortedKeys.length) {
    console.warn(
      `Length mismatch: sortedData has ${sortedData.length} items but sortedKeys has ${sortedKeys.length}. ` +
        `Falling back to numeric indices for missing keys.`,
    );
  }

  return sortedData.map((document, index) => ({
    documentId: document.documentId,
    key: sortedKeys[index] ?? index + 1,
  }));
};

/**
 * Merges and deduplicates mapped document IDs from existing and new arrays.
 * Uses O(n) Set-based deduplication instead of O(n²) nested loops.
 *
 * @param existingMapped - The array of existing mapped document IDs
 * @param newMapped - The array of new mapped document IDs to merge
 * @returns A merged and sorted array with duplicates removed based on documentId
 
 */
export const mergeMappedDocumentsIds = (
  existingMapped: MappedDocument[] | null | undefined,
  newMapped: MappedDocument[] | null | undefined,
): MappedDocument[] => {
  const existing = existingMapped || [];
  const newDocs = newMapped || [];

  if (!Array.isArray(existing) || !Array.isArray(newDocs)) {
    return [];
  }

  const newIds = new Set(newDocs.map((item) => item.documentId));

  const filteredExisting = existing.filter(
    (item) => !newIds.has(item.documentId),
  );

  return [...filteredExisting, ...newDocs].sort((a, b) =>
    numericComparator(a.documentId, b.documentId),
  );
};
